import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import type { ErrorLike } from "@apollo/client";
import type { ResultOf } from "@graphql-typed-document-node/core";
import type { Omittable } from "@salestrak/ui";

import { TeamWorkflowRosterDocument } from "#app/lib/data";
import { useDashboardScope } from "#app/widgets/dashboards/scope/dashboard-scope-context";

export type TeamScope = NonNullable<ResultOf<typeof TeamWorkflowRosterDocument>["team"]>;

/**
 * The team a board is scoped to, with its reach: the workflows it is granted
 * on and its leads + members. `teamId` is null off a team board, and `team`
 * is null until the read answers. A card that lists "where can this team's
 * work go" reads the workflow ids here rather than the workspace's whole
 * live set.
 */
export function useTeamScope(): Omittable<{
  teamId: string | null;
  team: TeamScope | null;
  loading: boolean;
  error?: ErrorLike;
}> {
  const scope = useDashboardScope();
  const teamId = scope?.entity?.type === "team" ? scope.entity.id : null;
  const variables = useMemo(() => ({ id: teamId ?? "" }), [teamId]);
  const { data, loading, error } = useQuery(TeamWorkflowRosterDocument, {
    variables,
    skip: !teamId,
    fetchPolicy: "cache-and-network",
  });
  const team = data?.team ?? null;
  return { teamId, team, loading: Boolean(teamId) && !team && loading, error };
}
